import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import { TypeAsserter } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as common from '../common'

import * as gql from './gql'
import type { Context, Entity, TypeParams } from './types'

const ATTRIBUTES: readonly ec.models.Attribute<TypeParams>[] = [
  ec.models.Attribute.fromKey<TypeParams>('priority', {
    name: '#',
    isSortable: true,
  }),
  ec.models.Attribute.fromKey<TypeParams>('name', {
    name: 'Name',
    isSortable: true,
  }),
  ...common.attributes.mapping.getAllAttributes<TypeParams['entity']>(
    entity => entity.mapping,
  ),
]

@ngCore.Injectable()
export class Api implements ec.models.Api<TypeParams> {
  /** @inheritdoc */
  public list = ec.models.Api.list.using.nothingButContext<TypeParams>(
    context => (context ? gql.queries.rules.executor(context) : rxjs.of([])),
  )

  public get: ec.models.Api<TypeParams>['get'] = (context, id) => {
    TypeAsserter.assertIsNotNullish(context)
    return gql.queries.rule.executor({ ...context, ruleId: id })
  }

  /** @inheritdoc */
  public getAttributes: ec.models.Api<TypeParams>['getAttributes'] = () =>
    rxjs.of(ATTRIBUTES)

  /** @inheritdoc */
  public create: ec.models.Api<TypeParams>['create'] = (
    context: Context,
    input: ents.extra.rate.gl.Rule.Input,
  ) => {
    TypeAsserter.assertIsNotNullish(context)
    return gql.mutations.createRule
      .executor({
        ...context,
        input: gql.inputs.ruleInput.TRANSFORMER.getGqlFrom(input),
      })
      .pipe(rxjs.map(rule => rule ?? _approximateRule(input)))
  }

  /** @inheritdoc */
  public batchCreate: ec.models.Api<TypeParams>['batchCreate'] = (
    context,
    inputs,
  ) => {
    TypeAsserter.assertIsNotNullish(context)
    return rxjs.forkJoin(
      inputs.map(input =>
        gql.mutations.createRule
          .executor({
            ...context,
            input: gql.inputs.ruleInput.TRANSFORMER.getGqlFrom(input),
          })
          .pipe(rxjs.map(rule => rule ?? _approximateRule(input))),
      ),
    )
  }

  /** @inheritdoc */
  public update: ec.models.Api<TypeParams>['update'] = (
    context,
    { id, updates },
  ) =>
    gql.mutations.updateRule.executor({
      ...TypeAsserter.validate(context, TypeAsserter.assertIsNotNullish),
      id,
      updates: gql.inputs.ruleUpdates.TRANSFORMER.getGqlFrom(updates),
    })

  /** @inheritdoc */
  public delete: ec.models.Api<TypeParams>['delete'] = (context, id) => {
    TypeAsserter.assertIsNotNullish(context)
    return gql.mutations.deleteRule.executor({
      ...context,
      id,
    })
  }

  /**
   * Executes {@link gql.mutations.applyRules} mutation.
   */
  public apply(context: Context): rxjs.Observable<void> {
    TypeAsserter.assertIsNotNullish(context)
    return gql.mutations.applyRules.executor(context)
  }
}

/**
 * On the off chance we don't receive a rule back from the backend, we can try
 * and approximate what it would like based on the request we made
 */
function _approximateRule(
  input: ents.extra.rate.gl.Rule.Input | Partial<Entity>,
): Entity {
  const {
    id = '???',
    filter = ents.extra.rate.gl.Filter.DEFAULT,
    isActive = true,
    mapping = { entries: [] },
    // NOTE: refreshing would probably remove the need to approximate the rule,
    // so it should fix the issue
    name = 'Something went wrong. Please refresh the page.',
    // NOTE: this should put it at the bottom of the list
    priority = 999,
  } = input as FlattenUnion<typeof input>
  return {
    filter,
    id,
    isActive,
    mapping,
    name,
    priority,
  }
}
